import { useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

const useFilterManager = (projectId, viewId) => {
  const storageKey = `filters:${projectId}:${viewId}`;

  const [savedFilters, setSavedFilters] = useState(() => {
    try {
      const item = localStorage.getItem(storageKey);
      const filters = item ? JSON.parse(item) : [];
      return filters.map(filter => ({ ...filter, notes: filter.notes || '' }));
    } catch (error) {
      console.error("Error reading saved filters from localStorage:", error);
      return [];
    }
  });

  const loadFiltersFromStorage = useCallback(() => {
    if (!projectId || !viewId) {
      setSavedFilters([]);
      return [];
    }
    try {
      const item = localStorage.getItem(storageKey);
      const filters = item ? JSON.parse(item) : [];
      setSavedFilters(
        filters
          .filter(f => f && typeof f === 'object') // Ensure f is a valid object
          .map(f => ({
            ...f,
            date: f.date ? new Date(f.date) : new Date(), // Handle potentially missing f.date
            data: Array.isArray(f.data) ? f.data : [],
            notes: f.notes || '' // Ensure notes field is present
          }))
      );
      return filters.map(filter => ({ ...filter, notes: filter.notes || '' })); // Return original parsed filters with notes field
    } catch (error) {
      console.error("Error loading filters from localStorage:", error);
      setSavedFilters([]);
      return [];
    }
  }, [storageKey, projectId, viewId]);

  useEffect(() => {
    if (projectId && viewId) {
      loadFiltersFromStorage();
    } else {
      // If projectId or viewId are not present, clear any existing filters
      // and ensure no attempt to load from a potentially invalid storageKey.
      setSavedFilters([]);
    }
    setSavedFilters(prevFilters =>
      prevFilters.map(filter => ({ ...filter, notes: filter.notes || '' }))
    );
  }, [loadFiltersFromStorage, projectId, viewId]);

  const saveFilterToStorage = useCallback((description, filterData, notes = '') => {
    if (!projectId || !viewId) {
      console.error("Project ID and View ID are required to save filters.");
      return null;
    }
    if (!description || !filterData) {
      console.error("Description and filter data are required to save a filter.");
      return null;
    }
    const coreFilterData = filterData.map(({ column, operator, value }) => ({ column, operator, value }));

    const newFilter = {
      id: uuidv4(),
      description,
      date: new Date().toISOString(), // Stored as ISO string
      data: coreFilterData,
      notes, // Add notes here
    };
    try {
      const currentFiltersRaw = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const currentFilters = currentFiltersRaw.filter(f => f && typeof f === 'object'); // Filter for valid objects
      const updatedFilters = [...currentFilters, newFilter];
      localStorage.setItem(storageKey, JSON.stringify(updatedFilters));
      setSavedFilters(
        updatedFilters
          .map(f => ({ // f here is guaranteed to be an object
            ...f,
            date: f.date ? new Date(f.date) : new Date(), // Convert date string (or fallback) to Date object for state
            data: Array.isArray(f.data) ? f.data : [],
            notes: f.notes || '' // Ensure notes field is present
          }))
      );
      return newFilter;
    } catch (error) {
      console.error("Error saving filter to localStorage:", error);
      return null;
    }
  }, [storageKey, projectId, viewId]);

  const deleteFilterFromStorage = useCallback((filterId) => {
    if (!projectId || !viewId) return;
    try {
      const currentFiltersRaw = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const currentFilters = currentFiltersRaw.filter(f => f && typeof f === 'object'); // Filter for valid objects
      const updatedFilters = currentFilters.filter(f => f.id !== filterId); // f is an object here
      localStorage.setItem(storageKey, JSON.stringify(updatedFilters));
      setSavedFilters(
        updatedFilters
          .map(f => ({ // f here is guaranteed to be an object
            ...f,
            date: f.date ? new Date(f.date) : new Date(), // Convert date string (or fallback) to Date object
            data: Array.isArray(f.data) ? f.data : [],
            notes: f.notes || '' // Ensure notes field is present
          }))
      );
    } catch (error) {
      console.error("Error deleting filter from localStorage:", error);
    }
  }, [storageKey, projectId, viewId]);

  const renameFilterInStorage = useCallback((filterId, newDescription) => {
    if (!projectId || !viewId) return;
    try {
      const currentFiltersRaw = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const currentFilters = currentFiltersRaw.filter(f => f && typeof f === 'object'); // Filter for valid objects
      const updatedFilters = currentFilters.map(f => // f is an object here
        f.id === filterId ? { ...f, description: newDescription, date: new Date().toISOString() } : f
      );
      localStorage.setItem(storageKey, JSON.stringify(updatedFilters));
      setSavedFilters(
        updatedFilters
          .map(f => ({ // f here is guaranteed to be an object
            ...f,
            date: f.date ? new Date(f.date) : new Date(), // Convert date string (or fallback) to Date object
            data: Array.isArray(f.data) ? f.data : [],
            notes: f.notes || '' // Ensure notes field is present
          }))
      );
    } catch (error) {
      console.error("Error renaming filter in localStorage:", error);
    }
  }, [storageKey, projectId, viewId]);

  const getFilters = useCallback(() => {
    try {
      const item = localStorage.getItem(storageKey);
      const filters = item ? JSON.parse(item) : [];
      // Ensure all filters have a notes field, defaulting to empty string if not present
      return filters.map(filter => ({ ...filter, notes: filter.notes || '' }));
    } catch (error) {
      console.error("Error retrieving filters from localStorage:", error);
      return [];
    }
  }, [storageKey]);

  return {
    savedFilters,
    loadFilters: loadFiltersFromStorage,
    saveFilter: saveFilterToStorage,
    deleteFilter: deleteFilterFromStorage,
    renameFilter: renameFilterInStorage,
    getFilters,
  };
};

export default useFilterManager;
